import type { CrawlAttempt } from '@zyx/shared';
import { useQuery } from '@tanstack/react-query';
import { ErrorState, LoadingRows } from '@/components/common/states';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useApi } from '@/hooks/use-connection';
import { describeTries, ITEM_KIND_LABELS, ITEM_STATUS_LABELS } from '@/lib/crawl-labels';
import { formatAgo } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ItemLabel } from './item-label';

const OUTCOME_STYLES: Readonly<Record<CrawlAttempt['outcome'], string>> = {
  succeeded: 'text-emerald-700 dark:text-emerald-400',
  failed: 'text-amber-700 dark:text-amber-400',
  gave_up: 'text-destructive',
  interrupted: 'text-muted-foreground',
};

export function Outcome(props: { readonly outcome: CrawlAttempt['outcome'] }) {
  return <span className={cn('font-medium', OUTCOME_STYLES[props.outcome])}>{props.outcome.replace('_', ' ')}</span>;
}

export function seconds(attempt: CrawlAttempt): string {
  return `${((attempt.finishedAt - attempt.startedAt) / 1000).toFixed(1)}s`;
}

/** One URL: its state, every run of it within retention, the page that found it and what it found. */
export function ItemDialog(props: { readonly itemId?: number; readonly onSelect: (itemId: number | undefined) => void }) {
  const api = useApi();
  const query = useQuery({ queryKey: ['crawl-item', props.itemId], queryFn: () => api.getCrawlItem(props.itemId ?? 0), enabled: props.itemId !== undefined });

  return (
    <Dialog open={props.itemId !== undefined} onOpenChange={(open) => (open ? undefined : props.onSelect(undefined))}>
      <DialogContent className="max-w-2xl">
        {query.isPending ? (
          <LoadingRows rows={4} />
        ) : query.isError ? (
          <ErrorState error={query.error} />
        ) : (
          <div className="space-y-4">
            <DialogHeader>
              <DialogTitle className="break-all font-mono text-sm">{query.data.item.target}</DialogTitle>
              <DialogDescription>
                {ITEM_KIND_LABELS[query.data.item.kind]} · {ITEM_STATUS_LABELS[query.data.item.status].toLowerCase()}, {describeTries(query.data.item)} · first queued{' '}
                {formatAgo(query.data.item.createdAt)}
              </DialogDescription>
            </DialogHeader>
            {query.data.item.subject === undefined ? null : (
              <div className="text-sm">
                <ItemLabel kind={query.data.item.kind} target={query.data.item.target} subject={query.data.item.subject} hideKind />
              </div>
            )}
            {query.data.item.lastError === undefined ? null : <p className="rounded bg-muted px-3 py-2 text-xs">{query.data.item.lastError}</p>}
            {query.data.item.parent === undefined ? null : (
              <div className="space-y-1.5">
                <h3 className="text-sm font-medium">Found by</h3>
                <button type="button" className="w-full rounded-md border border-border px-3 py-1.5 text-left text-xs hover:bg-muted" onClick={() => props.onSelect(query.data.item.parent?.itemId)}>
                  <ItemLabel kind={query.data.item.parent.kind} target={query.data.item.parent.target} subject={query.data.item.parent.subject} linkCode={false} />
                </button>
              </div>
            )}
            <section className="space-y-1.5">
              <h3 className="text-sm font-medium">Runs</h3>
              {query.data.attempts.length === 0 ? (
                <p className="text-sm text-muted-foreground">None within the history window.</p>
              ) : (
                <ul className="max-h-48 divide-y divide-border overflow-y-auto rounded-md border border-border text-xs">
                  {query.data.attempts.map((attempt) => (
                    <li key={attempt.attemptId} className="flex flex-wrap gap-x-3 px-3 py-1.5">
                      <span className="tabular-nums text-muted-foreground">{new Date(attempt.startedAt).toLocaleString()}</span>
                      <Outcome outcome={attempt.outcome} />
                      <span className="text-muted-foreground" title="Including any wait for the rate limit">
                        {seconds(attempt)}
                      </span>
                      {attempt.httpStatus === undefined ? null : <span>HTTP {attempt.httpStatus}</span>}
                      {attempt.queuedNew + attempt.queuedKnown === 0 ? null : (
                        <span>
                          found {attempt.queuedNew} new, {attempt.queuedKnown} known
                        </span>
                      )}
                      {attempt.error === undefined ? null : <span className="w-full text-muted-foreground">{attempt.error}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </section>
            {query.data.childCount === 0 ? null : (
              <section className="space-y-1.5">
                <h3 className="text-sm font-medium">
                  Found {query.data.childCount} URL{query.data.childCount === 1 ? '' : 's'}
                </h3>
                <ul className="max-h-64 divide-y divide-border overflow-y-auto rounded-md border border-border text-xs">
                  {query.data.children.map((child) => (
                    <li key={child.itemId}>
                      <button type="button" className="flex w-full items-center gap-3 px-3 py-1.5 text-left hover:bg-muted" onClick={() => props.onSelect(child.itemId)}>
                        <span className="min-w-0 flex-1">
                          <ItemLabel kind={child.kind} target={child.target} subject={child.subject} linkCode={false} />
                        </span>
                        <span className="shrink-0 text-muted-foreground">{ITEM_STATUS_LABELS[child.status]}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
